// Copyright 2020 Sorama B.V.

import {
    bindable,
    BindingEngine,
    bindingMode,
    computedFrom,
    customElement,
    Disposable,
    observable,
} from "aurelia-framework";
import { FileToUpload } from "controls/file-dropbox/FileDropbox";
import { FileDTO, ImageDTO, LinkedMediaObjectDTO, VideoDTO } from "datatypes/SoramaWebApi";
import { IPagedRequest } from "libraries/WebRequestHandler/IWebRequestHandler";
import { LinkedMediaObjectRepository, NewLinkedMediaObject } from "repositories/LinkedMediaObjectRepository";

interface ImageOrderMode {
    name: string;
    command: string;
    icon: string;
}

class ImageOrderModes {
    public static alphabeticalAscending: ImageOrderMode = {
        name: "Alphabetical Ascending",
        command: "$orderby = name asc",
        icon: "fas fa-sort-alpha-down",
    };
    public static alphabeticalDescending: ImageOrderMode = {
        name: "Alphabetical Descending",
        command: "$orderby = name desc",
        icon: "fas fa-sort-alpha-up",
    };
    public static uploadDateAscending: ImageOrderMode = {
        name: "Upload Date Ascending",
        command: "$orderby = created asc",
        icon: "fas fa-sort-numeric-down",
    };
    public static uploadDateDescending: ImageOrderMode = {
        name: "Upload Date Descending",
        command: "$orderby = created desc",
        icon: "fas fa-sort-numeric-up",
    };

    public static all: ImageOrderMode[] = [
        ImageOrderModes.alphabeticalAscending,
        ImageOrderModes.alphabeticalDescending,
        ImageOrderModes.uploadDateAscending,
        ImageOrderModes.uploadDateDescending,
    ];
}

@customElement("media-object-picker")
export class MediaObjectPicker {
    private imagePickerContainer: HTMLElement;
    private scrollContainer: HTMLElement;

    private disposables: Disposable[] = [];

    private requestPromise: Promise<unknown>;
    private deletePromise: Promise<void>;
    private scrollUpdateTimeout: number;

    private busyLoading: boolean;
    private uploading: boolean;
    private deleting: boolean;

    private imageOrderModes: ImageOrderModes[] = ImageOrderModes.all;

    @observable
    private imageOrderMode = ImageOrderModes.uploadDateDescending;

    @observable
    private selectedFile: FileList;

    @observable
    private zoomLevel = 4;

    private getRequest: IPagedRequest<ImageDTO> | IPagedRequest<VideoDTO> | IPagedRequest<FileDTO>;

    @computedFrom("zoomLevel", "imagePickerContainer.offsetWidth")
    private get imageSize(): number {
        if (!this.imagePickerContainer) return 0;

        return Math.floor((1 / this.zoomLevel) * this.imagePickerContainer.offsetWidth);
    }

    @computedFrom("scrollContainer.scrollHeight", "scrollContainer.scrollTop", "scrollContainer.offsetHeight")
    private get scrollPercentage(): number {
        if (!this.scrollContainer) {
            return 0;
        }

        if (this.scrollContainer.scrollHeight === 0) return 1;

        return (this.scrollContainer.scrollTop + this.scrollContainer.offsetHeight) / this.scrollContainer.scrollHeight;
    }

    protected getRequestItems: (ImageDTO | FileDTO | VideoDTO)[] = [];

    @bindable({})
    public mediaObjectType: "image" | "video" | "file";

    @bindable({ defaultBindingMode: bindingMode.twoWay })
    public selectedMediaObject: LinkedMediaObjectDTO;

    @computedFrom("mediaObjectType")
    public get inputAcceptType(): string {
        switch (this.mediaObjectType) {
            case "image":
                return "image/*";
            case "video":
                return "video/mp4";
            default:
                return "*.*";
        }
    }

    constructor(
        private readonly linkedMediaObjectRepository: LinkedMediaObjectRepository,
        private readonly bindingEngine: BindingEngine
    ) {}

    public bind(): void {
        const disposable = this.bindingEngine
            .propertyObserver(this, "scrollPercentage")
            .subscribe(this.scrollPercentageChanged);

        this.bindingEngine.propertyObserver(this, "mediaObjectType").subscribe(() => {
            this.refresh();
        });

        this.disposables.push(disposable);

        this.refresh();
    }

    public unbind(): void {
        while (this.disposables.length) this.disposables.pop().dispose();
    }

    private refresh(): void {
        const promise = this.requestPromise ? this.requestPromise : Promise.resolve();

        this.getRequestItems = [];

        // Update the request
        promise.then(() => {
            if (this.mediaObjectType == "image") {
                this.getRequest = this.linkedMediaObjectRepository.getImages(this.imageOrderMode.command);
            } else if (this.mediaObjectType == "video") {
                this.getRequest = this.linkedMediaObjectRepository.getVideos(this.imageOrderMode.command);
            } else if (this.mediaObjectType == "file") {
                this.getRequest = this.linkedMediaObjectRepository.getFiles(this.imageOrderMode.command);
            } else {
                this.getRequest = undefined;
            }

            this.loadNext();
        });
    }

    protected readonly selectMediaObject = (mediaObject: LinkedMediaObjectDTO): void => {
        if (this.deleting) {
            const promise = this.deletePromise ? this.deletePromise : Promise.resolve();

            this.deletePromise = promise.then(() => {
                return this.linkedMediaObjectRepository
                    .delete(mediaObject.id)
                    .then(() => {
                        const index = this.getRequest.total.findIndex((x) => x.id === mediaObject.id);
                        this.getRequest.total.splice(index, 1);

                        this.getRequestItems = this.getRequest.total;
                        if (this.selectedMediaObject) {
                            if (this.selectedMediaObject.id === mediaObject.id) {
                                this.selectedMediaObject = undefined;
                            }
                        }
                    })
                    .catch((e) => {
                        console.error("error is ", e);
                    });
            });
        } else {
            this.selectedMediaObject =
                this.selectedMediaObject && this.selectedMediaObject.id == mediaObject.id ? undefined : mediaObject;
        }
    };

    private zoomLevelChanged(): void {
        this.scrollSelectedImageIntoView();
    }

    private imageChanged(): void {
        this.scrollSelectedImageIntoView();
    }

    private scrollPercentageChanged = () => {
        this.loadNext();
    };

    protected upload = async (file: FileToUpload): Promise<void> => {
        this.busyLoading = true;
        this.uploading = true;

        const base64 = await file.dataAsBase64();
        const headerRegex = /data:.*;base64,/;
        const regexMatch = headerRegex.exec(base64);
        const data = regexMatch ? base64.substr(regexMatch[0].length) : base64;

        const newObject = {
            name: file.name,
            description: file.description,
            data: data,
            mimeType: file.mimeType,
        };

        let promise: Promise<NewLinkedMediaObject<VideoDTO | FileDTO | ImageDTO>>;

        switch (this.mediaObjectType) {
            case "image":
                promise = this.linkedMediaObjectRepository.createImage(newObject);
                break;
            case "file":
                promise = this.linkedMediaObjectRepository.createFile(newObject);
                break;
            case "video":
                promise = this.linkedMediaObjectRepository.createVideo(newObject);
                break;
        }

        return promise.then(() => {
            this.refresh();
        });
    };

    protected mediaObjectTypeChanged(): void {
        this.selectedMediaObject = undefined;
        this.deleting = false;
        this.refresh();
    }

    private toggleDeleteMode = (): void => {
        this.deleting = !this.deleting;
    };

    private scrollSelectedImageIntoView(): void {
        if (!this.getRequest || !this.imagePickerContainer) return;

        if (this.scrollUpdateTimeout) {
            window.clearTimeout(this.scrollUpdateTimeout);
            this.scrollUpdateTimeout = undefined;
        }

        // Find image in list
        const idx = this.getRequest.total.findIndex((x) => x === this.selectedMediaObject);

        if (idx === -1) return;

        const element = this.imagePickerContainer.children[idx];

        if (element) {
            this.scrollUpdateTimeout = window.setTimeout(() => {
                element.scrollIntoView(false);
                this.scrollUpdateTimeout = undefined;
            }, 250);
        }
    }

    private loadNext(): void {
        if (!this.getRequest || this.getRequest.finished || this.requestPromise) return;

        if (this.scrollPercentage > 0.8) {
            const offsetTop = this.scrollContainer.scrollTop;

            this.requestPromise = this.getRequest
                .next()
                .then((newImages) => {
                    this.getRequestItems.push(...newImages);

                    // Reset scrolltop to prevent the list from jumping back up
                    this.scrollContainer.scrollTop = Math.max(offsetTop, this.scrollContainer.scrollTop);
                    this.loadNext();
                })
                .catch((e) => {
                    console.error(e);
                })
                .then(() => {
                    this.requestPromise = undefined;
                });
        }
    }

    private floor(num: number): number {
        return Math.floor(num);
    }

    private ceil(num: number): number {
        return Math.ceil(num);
    }

    private imageOrderModeChanged(): void {
        if (this.getRequest) {
            this.refresh();
        }
    }
}
